/**
 *  @class
 *  @function ContrastRunnerGame
 */

// Brand red - obstacles fade toward this color as you collect power-ups,
// blending into the section's own background (now shifted red-on-the-right
// once the game is running, see startGame/endGame).
const CR_RIGHT_COLOR = { r: 0xe6, g: 0x23, b: 0x1e };

function crLerp(a, b, t) {
  return a + (b - a) * t;
}

// Two-bone inverse kinematics: given a root at (0,0) and a target,
// find the joint position so both bones keep their exact lengths.
// `bendDir` picks which of the two mirror solutions to use (+1 / -1),
// which is what makes a knee point forward and an elbow point back.
function crSolveIK(tx, ty, len1, len2, bendDir) {
  const reach = len1 + len2;
  let d = Math.hypot(tx, ty);
  // clamp the target inside reach so the limb never has to stretch
  if (d > reach * 0.999) {
    const s = (reach * 0.999) / d;
    tx *= s; ty *= s; d = reach * 0.999;
  }
  if (d < 0.001) d = 0.001;
  // distance along the root->target axis to the joint's projection
  const a = (d * d + len1 * len1 - len2 * len2) / (2 * d);
  const h = Math.sqrt(Math.max(0, len1 * len1 - a * a));
  const ux = tx / d, uy = ty / d;          // unit vector toward target
  const px = -uy * bendDir, py = ux * bendDir; // perpendicular
  return {
    jx: ux * a + px * h,
    jy: uy * a + py * h,
    tx, ty
  };
}

class ContrastRunnerGame {
  constructor(root) {
    this.root = root;
    // outer section wrapper whose background shifts (dark left / red
    // right) while a run is active - null-safe in case markup changes
    this.sectionRoot = root.closest('.fap');
    this.canvas = root.querySelector('.fap__game-canvas');
    this.ctx = this.canvas.getContext('2d');
    this.glassEffect = root.querySelector('.glass-effect');
    this.hud = root.querySelector('.fap__game-hud');
    this.scoreEl = root.querySelector('.fap__game-score');
    this.hudSub = root.querySelector('.fap__game-hud-sub');
    this.overlay = root.querySelector('.fap__game-overlay');
    this.startBtn = root.querySelector('.fap__game-start');

    // Player (avatar)
    this.playerW = 34;
    this.playerH = 44;
    this.gravity = 2600;
    this.jumpVel = -850;

    // Obstacles / powerups
    this.baseSpeed = 190;
    this.spawnClearance = 70;
    this.speedPerPowerup = 55;
    this.speedRampRate = 1.4; // how quickly speedBonus eases toward target
    this.pointsPerPowerup = 100;

    this.W = 0;
    this.H = 0;
    this.groundY = 0;

    this.playerY = 0;      // offset above ground (0 = on ground)
    this.velY = 0;
    this.onGround = true;
    this.animPhase = 0;    // drives the running-leg/arm cycle

    this.obstacles = [];
    this.spawnTimer = 0;
    this.spawnInterval = 1.5;

    this.powerups = [];
    this.powerupTimer = 0;
    this.powerupInterval = 6 + Math.random() * 3;
    this.powerupsCollected = 0;
    this.speedBonus = 0;       // current eased speed add-on
    this.targetSpeedBonus = 0; // where speedBonus is easing toward

    this.speed = this.baseSpeed;
    this.runTime = 0;
    this.score = 0;
    this.running = false;
    this.gameOver = false;
    this.lastTs = 0;

    this.boundLoop = this.loop.bind(this);

    this.resize();
    // The game box's height is no longer fixed - it's a grid row that
    // grows to fit the overlay's content (see .fap__game in the CSS), so
    // the canvas's own size can change for reasons other than a window
    // resize (e.g. the game-over text wrapping differently). ResizeObserver
    // catches all of those; window resize is a fallback for older browsers.
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.canvas);
    } else {
      window.addEventListener('resize', () => this.resize());
    }
    this.bindEvents();

    // initial paint behind the start overlay
    this.drawBackground();
    this.drawGround();
  }

  resize() {
    this.W = this.canvas.width = this.canvas.clientWidth;
    this.H = this.canvas.height = this.canvas.clientHeight;
    this.groundY = this.H * 0.72;
  }

  bindEvents() {
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Space') { e.preventDefault(); this.jump(); }
    });
    this.canvas.addEventListener('pointerdown', () => this.jump());
    this.startBtn.addEventListener('click', () => this.startGame());
  }

  playerX() {
    return this.W * 0.5;
  }

  // The canvas itself paints no background - the section behind it
  // supplies the dark/red backdrop (and shifts it on startGame/endGame),
  // so this just clears last frame's obstacles/player/ground.
  drawBackground() {
    this.ctx.clearRect(0, 0, this.W, this.H);
  }

  // Drawn after the obstacles so they can't paint over it - the floor
  // stays a continuous line across the whole screen
  drawGround() {
    const ctx = this.ctx;
    ctx.strokeStyle = 'rgba(128,128,128,0.35)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, this.groundY);
    ctx.lineTo(this.W, this.groundY);
    ctx.stroke();
  }

  // Player (avatar): always max contrast via white fill + black stroke
  drawPlayer() {
    const ctx = this.ctx;
    const cx = this.playerX();
    const feetBase = this.groundY - this.playerY;
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // white outline halo behind the black lines keeps the stickman
    // readable against either side of the fade, at any brightness
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 7;
    this.drawStickFigure(cx, feetBase);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 3;
    this.drawStickFigure(cx, feetBase);
    ctx.restore();
  }

  drawStickFigure(cx, feetBase) {
    const ctx = this.ctx;
    const headR = 8;
    const torsoLen = this.playerH * 0.34;
    const legLen = this.playerH * 0.42;
    const thighLen = legLen * 0.54;   // slight slack so the knee can bend
    const shinLen = legLen * 0.54;
    const armLen = this.playerH * 0.26;
    const upperArmLen = armLen * 0.55;
    const foreArmLen = armLen * 0.55;
    const strideLen = 13;
    const liftHeight = 9;
    const grounded = this.onGround;
    const phase = this.animPhase;
    // forward lean while running - torso, head and arms tilt toward the
    // direction of travel as one rigid group; more upright mid-air
    const leanAngle = grounded ? 0.42 : 0.15;
    // hip rises and falls twice per full stride (once per step)
    const hipBob = grounded ? Math.abs(Math.sin(phase)) * 2.5 : 0;
    const hipY = feetBase - legLen - hipBob;

    ctx.save();
    ctx.translate(cx, hipY);

    // Legs: a real gait cycle. Stance phase drives the foot backward
    // along the ground (that's the push-off), swing phase arcs it up and
    // forward for the next touchdown. The knee comes from IK, so the
    // thigh and shin never change length and the bend is always correct.
    for (let i = 0; i < 2; i++) {
      let footX, footY;
      if (grounded) {
        const ph = (phase + i * Math.PI) % (Math.PI * 2);
        if (ph < Math.PI) {
          // stance: planted, travelling from front to back
          const u = ph / Math.PI;
          footX = strideLen * (1 - 2 * u);
          footY = legLen;
        } else {
          // swing: lifts off the back, arcs forward, lands out front
          const u = (ph - Math.PI) / Math.PI;
          footX = -strideLen + 2 * strideLen * u;
          footY = legLen - Math.sin(Math.PI * u) * liftHeight;
        }
      } else {
        // tucked mid-air pose
        footX = i === 0 ? -6 : 9;
        footY = legLen - 8;
      }
      // bendDir -1 => knee leads forward, as a running knee should
      const leg = crSolveIK(footX, footY, thighLen, shinLen, -1);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(leg.jx, leg.jy);
      ctx.lineTo(leg.tx, leg.ty);
      ctx.stroke();
    }

    // torso + head + arms, all leaning forward together
    ctx.rotate(leanAngle);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, -torsoLen);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(0, -torsoLen - headR, headR, 0, Math.PI * 2);
    ctx.stroke();

    // Arms: swing opposite the same-side leg. Hand target orbits the
    // shoulder, elbow solved by IK with the bend pointing backward so
    // the arm always reads as a bent running arm.
    const shoulderY = -torsoLen * 0.78;
    const armSwing = 9;
    for (let i = 0; i < 2; i++) {
      const s = grounded
        ? Math.sin(phase + i * Math.PI + Math.PI)
        : (i === 0 ? 0.8 : -0.5);
      // hand stays fairly high and close to the body, sweeping fore/aft
      const handX = s * armSwing;
      const handY = armLen * 0.62 - Math.max(0, s) * 3;
      const arm = crSolveIK(handX, handY, upperArmLen, foreArmLen, 1);
      ctx.beginPath();
      ctx.moveTo(0, shoulderY);
      ctx.lineTo(arm.jx, shoulderY + arm.jy);
      ctx.lineTo(arm.tx, shoulderY + arm.ty);
      ctx.stroke();
    }

    ctx.restore();
  }

  spawnObstacle() {
    const h = 30 + Math.random() * 40;
    const w = 22 + Math.random() * 14;
    this.obstacles.push({ x: this.W + w, w, h });
  }

  spawnPowerup() {
    this.powerups.push({ x: this.W + 20, r: 15, collected: false });
  }

  // Keeps power-ups from spawning on top of (or too close to) an obstacle,
  // and vice versa, by checking horizontal clearance against whatever's
  // already near the spawn edge before adding a new one.
  clearOfObstacles(x, halfWidth) {
    for (const o of this.obstacles) {
      const oCenter = o.x + o.w / 2;
      if (Math.abs(x - oCenter) < (o.w / 2 + halfWidth + this.spawnClearance)) return false;
    }
    return true;
  }

  clearOfPowerups(x, halfWidth) {
    for (const p of this.powerups) {
      if (Math.abs(x - p.x) < (p.r + halfWidth + this.spawnClearance)) return false;
    }
    return true;
  }

  reset() {
    this.obstacles = [];
    this.powerups = [];
    this.spawnTimer = 0;
    this.spawnInterval = 1.5;
    this.powerupTimer = 0;
    this.powerupInterval = 6 + Math.random() * 3;
    this.powerupsCollected = 0;
    this.speedBonus = 0;
    this.targetSpeedBonus = 0;
    this.animPhase = 0;
    this.speed = this.baseSpeed;
    this.runTime = 0;
    this.score = 0;
    this.playerY = 0;
    this.velY = 0;
    this.onGround = true;
    this.gameOver = false;
    this.scoreEl.textContent = '0';
  }

  jump() {
    if (!this.running) return;
    if (this.gameOver) { this.startGame(); return; }
    if (this.onGround) {
      this.velY = this.jumpVel;
      this.onGround = false;
    }
  }

  startGame() {
    this.reset();
    this.overlay.classList.add('fap__game-hidden');
    if (this.glassEffect) this.glassEffect.classList.add('is-hidden');
    this.root.classList.add('is-playing');
    this.running = true;
    this.lastTs = performance.now();
    if (this.sectionRoot) this.sectionRoot.classList.add('fap--game-active');
    requestAnimationFrame(this.boundLoop);
  }

  endGame() {
    this.running = false;
    this.gameOver = true;
    this.root.classList.remove('is-playing');
    if (this.sectionRoot) this.sectionRoot.classList.remove('fap--game-active');
    if (this.glassEffect) this.glassEffect.classList.remove('is-hidden');
    this.overlay.classList.remove('fap__game-hidden');
    this.overlay.querySelector('.fap__game-overlay-heading').textContent = 'You blended in with an obstacle';
    this.overlay.querySelector('p').textContent =
      `You grabbed ${this.powerupsCollected} power-up${this.powerupsCollected === 1 ? '' : 's'} for ${Math.floor(this.score)} points. ` +
      `Low-contrast obstacles rely on their shape and motion to be noticed, ` +
      `same as poorly designed UI elements do for real users.`;
    this.startBtn.textContent = 'Run Again';
  }

  update(dt) {
    this.runTime += dt;
    this.scoreEl.textContent = Math.floor(this.score);

    // speed only comes from a slow base creep plus whatever powerups
    // you've collected - speedBonus eases toward its target rather than
    // snapping, so each pickup makes you *gradually* faster
    const baseRamp = Math.min(60, this.runTime * 2);
    this.speedBonus += (this.targetSpeedBonus - this.speedBonus) * Math.min(1, this.speedRampRate * dt);
    this.speed = this.baseSpeed + baseRamp + this.speedBonus;
    this.spawnInterval = Math.max(0.7, 1.5 - baseRamp * 0.006 - this.speedBonus * 0.004);

    // physics
    this.velY += this.gravity * dt;
    this.playerY -= this.velY * dt;
    if (this.playerY <= 0) {
      this.playerY = 0;
      this.velY = 0;
      this.onGround = true;
    }

    // running animation cadence scales with current speed, so strides
    // visibly quicken as power-ups push you faster
    this.animPhase += dt * (7 + this.speed * 0.02);

    // spawn
    this.spawnTimer += dt;
    if (this.spawnTimer >= this.spawnInterval) {
      if (this.clearOfPowerups(this.W + 30, 20)) {
        this.spawnTimer = 0;
        this.spawnObstacle();
      } else {
        this.spawnTimer = this.spawnInterval - 0.15; // retry shortly
      }
    }
    this.powerupTimer += dt;
    if (this.powerupTimer >= this.powerupInterval) {
      if (this.clearOfObstacles(this.W + 20, 15)) {
        this.powerupTimer = 0;
        this.powerupInterval = 6 + Math.random() * 3;
        this.spawnPowerup();
      } else {
        this.powerupTimer = this.powerupInterval - 0.15; // retry shortly
      }
    }

    // move obstacles & collision
    const px = this.playerX();
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const o = this.obstacles[i];
      o.x -= this.speed * dt;
      if (o.x + o.w < 0) { this.obstacles.splice(i, 1); continue; }

      const obLeft = o.x, obRight = o.x + o.w;
      const plLeft = px - this.playerW / 2, plRight = px + this.playerW / 2;
      const overlapX = obRight > plLeft && obLeft < plRight;
      if (overlapX) {
        const obTop = this.groundY - o.h;
        const plBottom = this.groundY - this.playerY;
        const hitVertically = plBottom > obTop + 4;
        if (hitVertically) {
          this.endGame();
        }
      }
    }

    // move powerups & collection (floats above the ground, simple circle
    // vs player-box overlap check)
    const puCenterY = this.groundY - this.playerH * 0.55;
    for (let i = this.powerups.length - 1; i >= 0; i--) {
      const p = this.powerups[i];
      p.x -= this.speed * dt;
      if (p.x + p.r < 0) { this.powerups.splice(i, 1); continue; }
      const plBottom = this.groundY - this.playerY;
      const plTop = plBottom - this.playerH;
      const overlapX = Math.abs(p.x - px) < (this.playerW / 2 + p.r);
      const overlapY = (puCenterY + p.r) > plTop && (puCenterY - p.r) < plBottom;
      if (overlapX && overlapY) {
        this.powerups.splice(i, 1);
        this.powerupsCollected++;
        this.targetSpeedBonus += this.speedPerPowerup;
        this.score = this.powerupsCollected * this.pointsPerPowerup;
      }
    }
  }

  drawObstacles() {
    const ctx = this.ctx;
    // Contrast starts a bit more visible, then degrades toward the
    // near-invisible level as you collect power-ups - maxed out (hardest)
    // once you've picked up 5. Rewards speed with reduced visibility.
    const startOffset = 34;
    const minOffset = 6;
    const t = Math.min(1, this.powerupsCollected / 5);
    const offset = crLerp(startOffset, minOffset, t);
    const obColor = {
      r: CR_RIGHT_COLOR.r - offset,
      g: CR_RIGHT_COLOR.g - offset,
      b: CR_RIGHT_COLOR.b - offset
    };
    for (const o of this.obstacles) {
      ctx.fillStyle = `rgb(${obColor.r | 0},${obColor.g | 0},${obColor.b | 0})`;
      // stop the fill a couple px short of groundY so the floor line
      // (2px wide, centered on groundY) stays fully visible underneath
      ctx.fillRect(o.x, this.groundY - o.h, o.w, o.h - 2);
    }
  }

  drawPowerups() {
    const ctx = this.ctx;
    const cy = this.groundY - this.playerH * 0.55;
    for (const p of this.powerups) {
      ctx.save();
      // max-contrast bolt icon: white fill + black stroke, same trick as
      // the stickman, so it reads instantly against either half of the screen
      ctx.translate(p.x, cy);
      const pulse = 1 + Math.sin(this.runTime * 8) * 0.06;
      ctx.scale(pulse, pulse);
      ctx.beginPath();
      ctx.arc(0, 0, p.r, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#000000';
      ctx.stroke();
      // bolt glyph
      ctx.beginPath();
      ctx.moveTo(-2, -8);
      ctx.lineTo(4, -1);
      ctx.lineTo(0, -1);
      ctx.lineTo(3, 8);
      ctx.lineTo(-5, 0);
      ctx.lineTo(-1, 0);
      ctx.closePath();
      ctx.fillStyle = '#000000';
      ctx.fill();
      ctx.restore();
    }
  }

  loop(ts) {
    if (!this.running) return;
    const dt = Math.min(0.033, (ts - this.lastTs) / 1000);
    this.lastTs = ts;

    this.update(dt);

    this.drawBackground();
    this.drawObstacles();
    this.drawGround();
    this.drawPowerups();
    this.drawPlayer();

    this.hud.classList.toggle('boosted', this.speedBonus > 4);
    this.hudSub.textContent = this.running
      ? `${this.powerupsCollected} power-up${this.powerupsCollected === 1 ? '' : 's'} • speed ${Math.round(this.speed)}`
      : 'space / tap / click to jump';

    requestAnimationFrame(this.boundLoop);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-contrast-runner]').forEach((root) => {
    new ContrastRunnerGame(root);
  });
});
